import { useState, useRef, useCallback, useEffect } from 'react';

interface UseLiveChatParams {
  canAdminSystem: boolean;
  sidebarView: string;
}

interface UseLiveChatReturn {
  liveChats: any[];
  selectedChat: string | null;
  setSelectedChat: React.Dispatch<React.SetStateAction<string | null>>;
  chatMessages: any[];
  setChatMessages: React.Dispatch<React.SetStateAction<any[]>>;
  chatInput: string;
  setChatInput: React.Dispatch<React.SetStateAction<string>>;
  chatSending: boolean;
  chatEndRef: React.RefObject<HTMLDivElement>;
  fetchLiveChats: () => Promise<void>;
  fetchChatMessages: (convId: string) => Promise<void>;
  acceptLiveChat: (convId: string) => Promise<void>;
  sendChatMessage: () => Promise<void>;
  resolveLiveChat: (convId: string) => Promise<void>;
}

const authHeaderJson = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json'
});

export function useLiveChat({
  canAdminSystem,
  sidebarView,
}: UseLiveChatParams): UseLiveChatReturn {
  const [liveChats, setLiveChats] = useState<any[]>([]);
  const [selectedChat, setSelectedChat] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null!);

  const fetchLiveChats = useCallback(async () => {
    if (!canAdminSystem) return;
    try {
      const res = await fetch('/api/ai/livechat/conversations?status=open&limit=50', { headers: authHeaderJson() });
      const data = await res.json();
      if (data.success) setLiveChats(data.conversations || []);
    } catch {}
  }, [canAdminSystem]);

  const fetchChatMessages = useCallback(async (convId: string) => {
    try {
      const res = await fetch(`/api/ai/livechat/conversations/${convId}`, { headers: authHeaderJson() });
      const data = await res.json();
      if (data.success) setChatMessages(data.messages || []);
    } catch {}
  }, []);

  const acceptLiveChat = useCallback(async (convId: string) => {
    try {
      await fetch(`/api/ai/livechat/conversations/${convId}/accept`, { method: 'PUT', headers: authHeaderJson(), body: '{}' });
      setSelectedChat(convId);
      await fetchLiveChats();
      await fetchChatMessages(convId);
    } catch {}
  }, [fetchLiveChats, fetchChatMessages]);

  const sendChatMessage = useCallback(async () => {
    if (!chatInput.trim() || !selectedChat || chatSending) return;
    setChatSending(true);
    try {
      await fetch(`/api/ai/livechat/conversations/${selectedChat}/message`, {
        method: 'POST', headers: authHeaderJson(), body: JSON.stringify({ content: chatInput.trim() })
      });
      setChatInput('');
      await fetchChatMessages(selectedChat);
    } catch {} finally { setChatSending(false); }
  }, [chatInput, selectedChat, chatSending, fetchChatMessages]);

  const resolveLiveChat = useCallback(async (convId: string) => {
    try {
      await fetch(`/api/ai/livechat/conversations/${convId}/resolve`, { method: 'PUT', headers: authHeaderJson(), body: '{}' });
      if (selectedChat === convId) { setSelectedChat(null); setChatMessages([]); }
      await fetchLiveChats();
    } catch {}
  }, [selectedChat, fetchLiveChats]);

  // Poll live chats when view is active
  useEffect(() => {
    if (sidebarView !== 'livechat' || !canAdminSystem) return;
    fetchLiveChats();
    const t = setInterval(fetchLiveChats, 5000);
    return () => clearInterval(t);
  }, [sidebarView, canAdminSystem, fetchLiveChats]);

  // Poll messages for selected chat
  useEffect(() => {
    if (!selectedChat || sidebarView !== 'livechat') return;
    fetchChatMessages(selectedChat);
    const t = setInterval(() => fetchChatMessages(selectedChat), 3000);
    return () => clearInterval(t);
  }, [selectedChat, sidebarView, fetchChatMessages]);

  // Auto-scroll chat messages
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  return {
    liveChats,
    selectedChat,
    setSelectedChat,
    chatMessages,
    setChatMessages,
    chatInput,
    setChatInput,
    chatSending,
    chatEndRef,
    fetchLiveChats,
    fetchChatMessages,
    acceptLiveChat,
    sendChatMessage,
    resolveLiveChat,
  };
}
